import React, { createContext, useContext, useState, ReactNode } from 'react';
import { TimePeriod } from '../types/api';

interface TimeFrameContextType {
  timeFrame: string;
  setTimeFrame: (timeFrame: string) => void;
  apiTimeFrame: TimePeriod;
}

const TimeFrameContext = createContext<TimeFrameContextType | undefined>(undefined);

export const useTimeFrame = () => {
  const context = useContext(TimeFrameContext);
  if (context === undefined) {
    throw new Error('useTimeFrame must be used within a TimeFrameProvider');
  }
  return context;
};

interface TimeFrameProviderProps {
  children: ReactNode;
}

// Utility function to convert UI timeFrame to API timeFrame format
const getApiTimeFrame = (period: string): TimePeriod => {
  switch (period) {
    case 'last1Month': return '1month';
    case 'last3Months': return '3months';
    case 'last6Months': return '6months';
    case 'lastYear': return '12months';
    default: return '6months';
  }
};

export const TimeFrameProvider: React.FC<TimeFrameProviderProps> = ({ children }) => {
  // Default to 6 months
  const [timeFrame, setTimeFrame] = useState<string>('last6Months');

  // Computed API time frame based on UI time frame
  const apiTimeFrame = getApiTimeFrame(timeFrame);

  const value = {
    timeFrame,
    setTimeFrame,
    apiTimeFrame
  };

  return (
    <TimeFrameContext.Provider value={value}>
      {children}
    </TimeFrameContext.Provider>
  );
};